import { IResource } from "@gettactic/api";
import { useResources } from "@gettactic/hooks";
import { useMapBuilderMappedResourcesData } from "@gettactic/map-builder/src/hooks/useMapBuilderMappedResourcesData";
import { MapInteractiveZones } from "@gettactic/map-builder/src/types";
import React from "react";

// Every resource type the map builder canvas can render as a spot. Mirrors
// the type list `useOfficeResources` passes to the same endpoint.
const EDITABLE_RESOURCE_TYPES = [
  "desk",
  "workspace",
  "meeting_room",
  "parking_lot",
  "parking_space"
];

/**
 * Loads the data a floor map editor needs to render an existing floor: the
 * office's resources, the floor's map data with canonical resource
 * labels/names stamped on, and the interactive-zone record that lets the
 * canvas tell walk-up spots apart while editing. Shared by every editor that
 * opens an existing floor, so they stay consistent.
 *
 * Uses `useResources` directly rather than the heavier `useOfficeResources`
 * — the editor has no date/schedule context (see the interactiveZones note
 * below), so it doesn't need `useOfficeResources`'s "occupied" schedules
 * fetch, which requires a valid date and errors without one.
 */
export const useFloorEditorCanvasData = (
  officeId: string,
  floor?: IResource
) => {
  const { resources: allResources, loading: isResourcesLoading } = useResources(
    {
      officeId,
      types: EDITABLE_RESOURCE_TYPES
    }
  );
  const officeResources = allResources?.elements;

  const floorData = useMapBuilderMappedResourcesData(floor, officeResources);

  // The editing canvas doesn't show live occupancy (no date/schedule
  // context, no click-to-reserve) — every spot renders with the same
  // neutral "available" look regardless of its resource's real state. The
  // one exception is walk-up, which is a resource *type* rather than live
  // occupancy: it's otherwise impossible to tell a walk-up spot apart from
  // a regular one while editing. Every other field below is left at its
  // inert default on purpose so this stays scoped to just that.
  const interactiveZones = React.useMemo<
    MapInteractiveZones | undefined
  >(() => {
    if (!officeResources) return undefined;
    const record: MapInteractiveZones = {};
    for (const res of officeResources) {
      record[res.id] = {
        zoneId: res.id,
        notAssigned: false,
        requiresApproval: false,
        isWalkUp: !!res.is_walkup,
        disabled: false,
        isOccupied: false,
        reservedSlots: 0,
        hasSchedules: false,
        showCircleProfilePhoto: false,
        showPulse: false
      };
    }
    return record;
  }, [officeResources]);

  return { officeResources, isResourcesLoading, floorData, interactiveZones };
};
